import { call, put, select, takeEvery } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { CodebookEntry } from 'generated/bffCodebook/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { selectProperty } from 'saga/property/ducks';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';

export interface CodebookEntryValue {
  code: string;
  codeLocale: {
    attributeTextExt: string;
  };
  parameters: Record<string, { value: string; seq: number }>;
}

/* STATE */
export interface CodebookState {
  [key: string]: {
    value: CodebookEntryValue[];
    expire: number;
  };
}

/* ACTION TYPES */
export enum CodebookActionTypes {
  GetCodebook = '@@Codebook/GET_CODEBOOK',
}

/* ACTIONS */
export const getCodebookActions = createApiActionCreators(CodebookActionTypes.GetCodebook);

/* REDUCERS */
const initialState: CodebookState = {};

export default createReducer(initialState, {
  [CodebookActionTypes.GetCodebook]: {
    [RequestActionTypes.SUCCESS]: (
      state: Record<string, any>,
      payload: { codebookType: string; expire: number; entries: CodebookEntry[] }
    ) => ({
      ...state,
      [payload.codebookType]: {
        expire: payload.expire,
        value: payload.entries.map((entry) => ({
          code: entry.code,
          codeLocale: entry.codeLocale,
          parameters: entry.parameters?.reduce((acc, val) => {
            const { name, ...attrs } = val;

            acc[name] = attrs;

            return acc;
          }, {} as Record<string, any>),
        })),
      },
    }),
  },
});

/* SELECTORS */
export const selectCodebooks = (state: AppState) => state.codebook;

export const selectCodebook = (state: AppState, codebookType: string) => selectCodebooks(state)[codebookType]?.value;

/* SAGAS */
export function* getCodebookValue(codebookType: string) {
  const cbValue: CodebookState = yield select(selectCodebook, codebookType);

  if (cbValue && cbValue[codebookType] && cbValue[codebookType].expire > new Date().getTime()) {
    return cbValue;
  }

  const ttl = Number(selectProperty('bff.response.cacheTTL.codebook'));

  const request: ApiClientRequest = {
    apiCall: call(api.getCodebook, yield getApiUrl(environment.urlConfig.codebook), codebookType),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getCodebookActions.success({
        codebookType,
        entries: resp.data.codebookEntryList,
        expire: new Date().getTime() + ttl,
      })
    );

    return resp.data;
  }

  return null;
}

function* getCodebook({ payload: codebookType }: any) {
  const cbValue: CodebookState = yield select(selectCodebook, codebookType);

  if (cbValue && cbValue[codebookType] && cbValue[codebookType].expire > new Date().getTime()) {
    return;
  }

  const ttl = Number(selectProperty('bff.response.cacheTTL.codebook'));
  const request: ApiClientRequest = {
    apiCall: call(api.getCodebook, yield getApiUrl(environment.urlConfig.codebook), codebookType),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getCodebookActions.success({
        codebookType,
        entries: resp.data.codebookEntryList,
        expire: new Date().getTime() + ttl,
      })
    );
  } else {
    yield put(getCodebookActions.failure());
  }
}

/* EXPORT */
export function* codebookSaga() {
  yield takeEvery(createActionType(CodebookActionTypes.GetCodebook, RequestActionTypes.REQUEST), getCodebook);
}
